import React, { useRef, useState } from "react";
import { Modal } from "newui/components/ui/Modal";
import { InputWithLabel } from "newui/components/ui/InputWithLabel";
import { useMfaDisable } from "../../hooks/useMfaDisable";

export interface DisableMfaModalProps {
	isOpen: boolean;
	onClose: () => void;
	onDisabled: () => void;
}

export const DisableMfaModal = ({ isOpen, onClose, onDisabled }: DisableMfaModalProps) => {
	const [password, setPassword] = useState("");
	const disableMfa = useMfaDisable();

	// PRO-731 follow-up — land focus on the password box instead of the modal's
	// close button (Chakra's focus lock otherwise focuses the first tabbable
	// element). Same mechanism as ScoringModal; this modal owns its own Modal and
	// the field is mounted at open time, so `initialFocusRef` is all that's needed.
	const passwordRef = useRef<HTMLInputElement>(null);

	const handleClose = () => {
		setPassword("");
		disableMfa.reset();
		onClose();
	};

	const handleConfirm = () => {
		disableMfa.mutate(
			{ password },
			{
				onSuccess: () => {
					setPassword("");
					onDisabled();
				},
			}
		);
	};

	// PRO-731 follow-up (item 5) — this password field isn't inside a `<form>`
	// (the primary action lives in the shared Modal's `primaryButton`), so
	// there is nothing for the browser to submit on Enter. preventDefault +
	// the same guard as the button itself (`password`/`isPending`) stops a
	// double-submit while a request is already in flight.
	const handlePasswordKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key !== "Enter") {
			return;
		}
		e.preventDefault();
		if (password && !disableMfa.isPending) {
			handleConfirm();
		}
	};

	return (
		<Modal
			isOpen={isOpen}
			onClose={handleClose}
			initialFocusRef={passwordRef}
			title="Disable two-factor authentication"
			description="Your account will no longer require a code at sign-in. Confirm your password to continue."
			iconType="warning"
			modalType="alert"
			closeOnEsc
			primaryButton={{
				text: "Disable",
				btntype: "danger",
				disabled: !password,
				isLoading: disableMfa.isPending,
				onClick: handleConfirm,
			}}
			secondaryButton={{
				text: "Cancel",
				onClick: handleClose,
			}}
		>
			<InputWithLabel
				label="Password"
				type="password"
				icon
				inputRef={passwordRef}
				value={password}
				onChange={(e) => setPassword(e.target.value)}
				onKeyDown={handlePasswordKeyDown}
				errorText={disableMfa.error?.message}
				testId="disable-2fa-password-input"
				required
			/>
		</Modal>
	);
};
